import { isDev, isPostHogEnabled } from "./enabled"
import type {
  AnalyticsEvent,
  AnalyticsEventMap,
  PersonProperties,
} from "./events"
import { consoleProvider } from "./providers/console"
import { noopProvider } from "./providers/noop"
import { posthogClientProvider } from "./providers/posthog-client"
import type { AnalyticsProvider } from "./types"

// Single switch, chosen ONCE. posthog-js manages the current distinctId, so
// client capture/exception don't take one. Fire-and-forget; never throws.
const provider: AnalyticsProvider = isPostHogEnabled()
  ? posthogClientProvider
  : isDev()
    ? consoleProvider
    : noopProvider

function guard(fn: () => void, label: string) {
  try {
    fn()
  } catch (err) {
    console.error(`[analytics] ${label} failed:`, err)
  }
}

export const analytics = {
  capture<E extends AnalyticsEvent>(
    event: E,
    properties: AnalyticsEventMap[E]
  ) {
    guard(() => provider.capture(undefined, event, properties), "capture")
  },

  identify(args: { distinctId: string; properties?: PersonProperties }) {
    guard(() => provider.identify(args.distinctId, args.properties), "identify")
  },

  captureException(error: unknown, properties?: Record<string, unknown>) {
    guard(
      () => provider.captureException(error, undefined, properties),
      "captureException"
    )
  },
}
